import { observer } from 'mobx-react-lite';
import View from '@components/view/view.tsx';
import ViewPanel from '@components/view/view-panel.tsx';
import Icon from '@components/core/icon/icon.tsx';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import authStore from '@store/auth-store.ts';
import Button from '@components/core/button/button.tsx';
import { useState } from 'react';
import {
	CookiePreferences,
	LocalStorageKey,
} from '@/enums/local-storage-types.ts';
import cookiePromptStore from '@store/cookie-prompt-store.ts';
import postHogService from '@services/posthog-service.ts';
import localStorageService from '@services/local-storage-service.ts';
import CookieSlideToggles from '@components/core/cookie-prompt/cookie-slide-toggles.tsx';

const sessionTrackingEnabled = () => {
	const cookiePreferencesString = localStorageService.get(
		LocalStorageKey.CookiePreferences
	);

	if (cookiePreferencesString) {
		try {
			const cookiePreferences = JSON.parse(
				cookiePreferencesString
			) as CookiePreferences;

			return cookiePreferences.st;
		} catch (_) {
			// Do nothing
		}
	}

	return true;
};

const Settings = observer(function Settings() {
	const user = authStore.user;

	const [loadingResetPassword, setLoadingResetPassword] = useState(false);
	const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
	const [sessionTracking, setSessionTracking] = useState(
		sessionTrackingEnabled()
	);

	const requestPasswordChange = async () => {
		const user = authStore.user;

		if (!user) {
			return;
		}

		setLoadingResetPassword(true);

		const resp = await authStore.requestPasswordChange(user.email);
		if (resp.ok) {
			setPasswordResetSuccess(true);
		}

		setLoadingResetPassword(false);
	};

	const toggleSessionTracking = (on: boolean) => {
		setSessionTracking(on);

		const cookiePreferences: CookiePreferences = {
			st: on,
		};

		cookiePromptStore.setCookiePreferences(cookiePreferences);

		if (cookiePreferences.st) {
			postHogService.enablePersistentTracking();
		} else {
			postHogService.disablePersistentTracking();
			cookiePromptStore.deleteSessionTrackingCookie();
		}
	};

	if (!user) {
		return <View>Not logged in.</View>;
	}

	return (
		<View className={'max-w-(--breakpoint-lg)'} hideHeader>
			<ViewPanel>
				<div
					className={
						'flex justify-evenly items-center w-full gap-4 mb-10 flex-wrap'
					}
				>
					<div
						className={
							'flex justify-center items-center gap-4 flex-wrap'
						}
					>
						<Icon
							icon={IconEnum.ACCOUNT}
							size={'5rem'}
							className={'min-w-20'}
						/>

						<div className={'grid grid-cols-[auto_1fr] gap-x-2'}>
							<span>Name:</span>
							<span
								className={'break-all'}
							>{`${user.first_name} ${user.last_name}`}</span>

							<span>E-mail:</span>
							<span className={'break-all'}>{user.email}</span>

							<span>Joined:</span>
							<span className={'break-all'}>
								{new Date(user.created).toLocaleDateString()}
							</span>
						</div>
					</div>

					<div className={'flex justify-center items-center'}>
						{!passwordResetSuccess && (
							<Button
								title={'Request Password Change'}
								onClick={() => {
									void requestPasswordChange();
								}}
								loading={loadingResetPassword}
								small
							/>
						)}

						{passwordResetSuccess && (
							<div className={'w-full text-center max-w-sm'}>
								{
									'We’ve emailed you a link to reset your password. Please check your inbox (or spam folder) and follow the instructions.'
								}
							</div>
						)}
					</div>
				</div>

				<div
					className={
						'w-full flex justify-center items-start flex-col gap-4 mb-4'
					}
				>
					<div className={'text-xl'}>Cookie Preferences</div>

					<div
						className={
							'w-full flex justify-center items-center flex-col gap-4'
						}
					>
						<CookieSlideToggles
							sessionTracking={{
								initialState: sessionTracking,
								onToggle: toggleSessionTracking,
							}}
						/>
					</div>
				</div>
			</ViewPanel>
		</View>
	);
});

export default Settings;
